import React, { useState } from 'react';
import { Search, Bell, Sun, Moon, ChevronDown } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import type { DashboardView } from './Sidebar';
const viewLabels: Record<DashboardView, string> = {
  overview: 'Overview',
  transactions: 'Transactions',
  budget: 'Budget Planner',
  investments: 'Investments',
  goals: 'Financial Goals',
  credit: 'Credit & Loans',
  settings: 'Settings'
};
interface TopBarProps {
  activeView: DashboardView;
  sidebarCollapsed: boolean;
}
export function TopBar({
  activeView,
  sidebarCollapsed
}: TopBarProps) {
  const {
    isDark,
    toggleTheme
  } = useTheme();
  const [showNotifications, setShowNotifications] = useState(false);
  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });
  return <header className={`fixed top-0 right-0 h-16 z-20 flex items-center gap-4 px-6 bg-white dark:bg-dark-surface border-b border-light-border dark:border-dark-border transition-all duration-300 ${sidebarCollapsed ? 'left-16' : 'left-60'}`} data-id="element-220">
      {/* Page title */}
      <div className="flex-1" data-id="element-221">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-222">
          {viewLabels[activeView]}
        </h1>
        <p className="text-xs text-gray-400" data-id="element-223">{today}</p>
      </div>

      {/* Search */}
      <div className="hidden md:flex items-center gap-2 bg-light-hover dark:bg-dark-hover rounded-lg px-3 py-2 w-64 border border-light-border dark:border-dark-border focus-within:border-primary-400 transition-colors" data-id="element-224">
        <Search size={15} className="text-gray-400 shrink-0" data-id="element-225" />
        <input type="text" placeholder="Search transactions..." className="bg-transparent text-sm text-gray-700 dark:text-gray-300 placeholder-gray-400 outline-none w-full" data-id="element-226" />
      </div>

      {/* Notifications */}
      <div className="relative" data-id="element-227">
        <button onClick={() => setShowNotifications(!showNotifications)} className="relative p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-hover text-gray-500 dark:text-gray-400 transition-colors" data-id="element-228">
          <Bell size={18} data-id="element-229" />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-accent-500 rounded-full" data-id="element-230" />
        </button>
        {showNotifications && <div className="absolute right-0 top-full mt-2 w-72 bg-white dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl shadow-xl p-4 z-50" data-id="element-231">
            <p className="text-sm font-semibold text-gray-900 dark:text-white mb-3" data-id="element-232">
              Notifications
            </p>
            {[{
          icon: '⚠️',
          text: 'Shopping budget is 105% used',
          time: '2h ago',
          color: 'text-red-500'
        }, {
          icon: '🎯',
          text: 'Emergency Fund is 65% complete!',
          time: '1d ago',
          color: 'text-primary-500'
        }, {
          icon: '📈',
          text: 'Portfolio up 1.2% today',
          time: '3h ago',
          color: 'text-accent-600'
        }].map((n, i) => <div key={i} className="flex items-start gap-3 py-2 border-b border-light-border dark:border-dark-border last:border-0" data-id="element-233">
                <span className="text-lg" data-id="element-234">{n.icon}</span>
                <div data-id="element-235">
                  <p className={`text-xs font-medium ${n.color}`} data-id="element-236">{n.text}</p>
                  <p className="text-xs text-gray-400" data-id="element-237">{n.time}</p>
                </div>
              </div>)}
          </div>}
      </div>

      {/* Theme toggle */}
      <button onClick={toggleTheme} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-hover text-gray-500 dark:text-gray-400 transition-colors" title={isDark ? 'Switch to light mode' : 'Switch to dark mode'} data-id="element-238">
        {isDark ? <Sun size={18} data-id="element-239" /> : <Moon size={18} data-id="element-240" />}
      </button>

      {/* User */}
      <button className="flex items-center gap-2 pl-2 border-l border-light-border dark:border-dark-border" data-id="element-241">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent-400 to-accent-600 flex items-center justify-center text-white text-sm font-bold" data-id="element-242">
          A
        </div>
        <ChevronDown size={14} className="text-gray-400" data-id="element-243" />
      </button>
    </header>;
}